// OPT-317: selection and payload helpers for "Print labels" on the Order queue.
// Pure, so the table can stay a thin view over them.

/** The distinct orderIds of `rows`, in row order. */
export function selectableOrderIds(rows) {
  const seen = new Set();
  const out = [];
  for (const r of rows || []) {
    if (!r || !r.orderId || seen.has(r.orderId)) continue;
    seen.add(r.orderId);
    out.push(r.orderId);
  }
  return out;
}

/**
 * orderId -> every item its parcel ships, as { sku, location } in row order
 * ("" for an item with no SKU or location). Removed lines do not ship. The one
 * place that decides what goes on a label's SKU/LOC strip, for the bulk print
 * and the single label button alike.
 */
export function labelItemsByOrder(rows) {
  const out = new Map();
  for (const r of rows || []) {
    if (!r || r.removed || !r.orderId) continue;
    if (!out.has(r.orderId)) out.set(r.orderId, []);
    out.get(r.orderId).push({ sku: r.sku || "", location: r.location || "" });
  }
  return out;
}

/**
 * A parcel's print items; a missing SKU or location is sent as null (the strip
 * prints "No SKU" / "LOC: none").
 */
export function parcelItems(items) {
  return (items || []).map((i) => ({ sku: (i && i.sku) || null, location: (i && i.location) || null }));
}

/**
 * One { orderId, items:[{sku}] } per selected order.
 *
 * Parcel order is each order's first row in `visibleRows` (the queue's sort,
 * bundles kept together). Items come from `allRows`, not the filtered rows, so
 * a search that hides one item of a bundle does not drop it from the strip.
 */
export function parcelsFor(visibleRows, allRows, selectedIds) {
  const selected = selectedIds || {};
  const items = labelItemsByOrder(allRows);
  return selectableOrderIds(visibleRows)
    .filter((id) => selected[id] && items.has(id))
    .map((id) => ({ orderId: id, items: parcelItems(items.get(id)) }));
}

/** Drops ids that no longer appear in `rows`. Returns a new object set. */
export function pruneSelection(selectedIds, rows) {
  const present = new Set(selectableOrderIds(rows));
  const out = {};
  for (const id of Object.keys(selectedIds || {})) {
    if (selectedIds[id] && present.has(id)) out[id] = true;
  }
  return out;
}

const NO_LABEL_BY_MARKETPLACE = ["ebay", "medusa", "consigncloud", "shopify"];

/** Seller-facing text for a skip reason from the print resolver. */
export function skipMessage(reason, channel) {
  switch (reason) {
    case "NO_LABEL":
      return NO_LABEL_BY_MARKETPLACE.includes(channel)
        ? "this marketplace doesn't supply a label"
        : "label not stored yet";
    case "NOT_FOUND": return "not found";
    case "LABEL_MISSING": return "stored label is missing";
    case "UNPRINTABLE": return "label file can't be printed";
    case "UNAVAILABLE": return "label temporarily unavailable";
    default: return "couldn't be printed";
  }
}
